import { useTranslation } from 'next-i18next';
import { NoSSR } from '@/components';
import { useLocale } from '@/hooks';
import { BestUserIcon } from '@/layout/Header/icons/BestUserIcon';
import { TeacherReviewIcon } from '@/layout/Header/icons/TeacherReviewIcon';
import { MobileDrawer } from '@/layout/Header/MobileDrawer/MobileDrawer';
import { AuthButton } from '@/layout/Header/UserManagementButton';
import { FirstPurchaseTooltip } from '@/shared/components/FirstPurchaseTooltip/FirstPurchaseTooltip';
import { GnbLanguageSelector } from '@/shared/components/LanguageSelector';
import { useCountryCode } from '@/shared/hooks';
import { HomeLogo } from '../HomeLogo';
import { getSupportLinks } from './constants/gnbConstants';
import { useSchoolTypeDropdown } from './hooks/useSchoolTypeDropdown';
import { BlogIcon } from './icons/Blog';
import { Event } from './icons/Event';
import { FAQ } from './icons/FAQ';
import { Notice } from './icons/Notice';
import { QuizForBizIcon } from './icons/QuizForBiz';
import { UpdateNews } from './icons/UpdateNews';
import { UserGuide } from './icons/UserGuide';
import { CreateButton } from './CreateButton';
import { type GnbButtonType } from './GnbButtonType';
import { HeaderContainer } from './HeaderContainer';
import { LeftButtons } from './LeftButtons';
import { EnterCodeButton, ExploreButton } from './PageNavigatorButton';
export const filterVisibleButtons = (buttons: GnbButtonType[]) => {
  return buttons.filter(button => !button.isHidden).map(button => {
    if (button.subButtons) {
      button.subButtons = button.subButtons.filter(subButton => !subButton.isHidden);
    }
    return button;
  });
};
export function GnbHeader() {
  const {
    t
  } = useTranslation(['common']);
  const {
    locale
  } = useLocale();
  const {
    dropdownButton
  } = useSchoolTypeDropdown();
  const {
    countryCode,
    isReady,
    isKR
  } = useCountryCode();
  const 한국을_제외하고_숨겨야하는_메뉴들 = locale !== 'ko';
  const 한국국가만_표시 = isReady && isKR;
  const supportLinks = getSupportLinks(countryCode);
  const GNB_BUTTONS: GnbButtonType[] = [한국국가만_표시 ? dropdownButton : {
    label: t('header.explore.button'),
    href: '/public',
    clickEventname: 'gnb_public_click'
  }, {
    label: t('common.header.pricingGuide'),
    href: '/pricing',
    clickEventname: 'gnb_pricing_click',
    wrapper: children => <FirstPurchaseTooltip>{children}</FirstPurchaseTooltip>
  }, {
    label: t('header.useCase'),
    clickEventname: 'gnb_use_case_click',
    subButtons: [{
      label: t('header.useCase.blog'),
      icon: <BlogIcon />,
      href: t('common.quizBlogLink'),
      target: '_blank',
      clickEventname: 'gnb_blog_click',
      sectionTitle: t('header.useCase.educational')
    }, {
      label: '선생님 후기',
      icon: <TeacherReviewIcon />,
      href: 'https://zep-quiz-teachers-review.super.site/',
      target: '_blank',
      clickEventname: 'gnb_review_click',
      isHidden: 한국을_제외하고_숨겨야하는_메뉴들
    }, {
      label: '우수 활동자 사례',
      icon: <BestUserIcon />,
      href: 'https://zep-quiz-teachers-review.super.site/%ec%9a%b0%ec%88%98-%ed%99%9c%eb%8f%99%ec%9e%90-%ec%86%8c%ea%b0%9c',
      target: '_blank',
      clickEventname: 'gnb_best_user_click',
      isHidden: 한국을_제외하고_숨겨야하는_메뉴들
    }, {
      label: t('header.useCase.quizForBiz'),
      icon: <QuizForBizIcon />,
      href: `${process.env.NEXT_PUBLIC_BASE_URL}/biz`,
      target: '_blank',
      clickEventname: 'gnb_business_click',
      sectionTitle: t('header.useCase.corporate')
    }]
  }, {
    label: t('common.header.customerSupport'),
    clickEventname: 'gnb_customer_support_click',
    subButtons: [{
      label: t('home.pricing.frequentlyQuestion'),
      icon: <FAQ />,
      href: supportLinks.faq,
      target: '_blank',
      clickEventname: 'gnb_faq_click'
    }, {
      label: t('common.header.userGuide'),
      icon: <UserGuide />,
      href: supportLinks.guide,
      target: '_blank',
      clickEventname: 'gnb_guide_click'
    }, {
      label: t('common.header.updateNews'),
      icon: <UpdateNews />,
      href: supportLinks.updateNews,
      target: '_blank',
      clickEventname: 'gnb_update_news_click'
    }, {
      label: t('gnb.header.event'),
      icon: <Event />,
      href: supportLinks.event,
      target: '_blank',
      clickEventname: 'gnb_event_click',
      isHidden: !한국국가만_표시
    }, {
      label: t('common.header.notice'),
      icon: <Notice />,
      href: supportLinks.notice,
      target: '_blank',
      clickEventname: 'gnb_notice_click'
    }]
  }];
  const MOBILE_GNB_BUTTONS: GnbButtonType[] = [{
    label: t('header.explore.button'),
    href: '/public',
    clickEventname: 'gnb_public_click'
  }, {
    label: t('common.header.pricingGuide'),
    href: '/pricing',
    clickEventname: 'gnb_pricing_click'
  }, {
    label: t('common.header.notice'),
    icon: <Notice />,
    href: supportLinks.notice,
    target: '_blank',
    clickEventname: 'gnb_notice_click'
  }, {
    label: t('common.header.userGuide'),
    icon: <UserGuide />,
    href: supportLinks.guide,
    target: '_blank',
    clickEventname: 'gnb_guide_click'
  }];
  return <HeaderContainer className="justify-start" data-sentry-element="HeaderContainer" data-sentry-component="GnbHeader" data-sentry-source-file="GnbHeader.tsx">
      <HomeLogo location={'header'} data-sentry-element="HomeLogo" data-sentry-source-file="GnbHeader.tsx" />
      <div className="ml-[24px] hidden items-center gap-2xs overflow-x-auto tablet-sm:flex [&>*]:shrink-0">
        <LeftButtons buttons={filterVisibleButtons(GNB_BUTTONS)} data-sentry-element="LeftButtons" data-sentry-source-file="GnbHeader.tsx" />
      </div>
      <div className="ml-auto flex shrink-0 items-center gap-2xs">
        <div className={'flex tablet-sm:hidden'}>
          <ExploreButton.Icon data-sentry-element="unknown" data-sentry-source-file="GnbHeader.tsx" />
        </div>
        <div className={'hidden gap-xs tablet-sm:flex tablet-sm:items-center'}>
          <EnterCodeButton type={'normal'} size="sm" data-sentry-element="EnterCodeButton" data-sentry-source-file="GnbHeader.tsx" />
          <CreateButton size="sm" data-sentry-element="CreateButton" data-sentry-source-file="GnbHeader.tsx" />
          <AuthButton size="sm" data-sentry-element="AuthButton" data-sentry-source-file="GnbHeader.tsx" />
          <GnbLanguageSelector size="sm" iconSize="sm" data-sentry-element="GnbLanguageSelector" data-sentry-source-file="GnbHeader.tsx" />
        </div>

        <NoSSR fallback={<MobileDrawer.TriggerContent />} data-sentry-element="NoSSR" data-sentry-source-file="GnbHeader.tsx">
          <MobileDrawer buttons={filterVisibleButtons(MOBILE_GNB_BUTTONS)} footerBottomAccessory={<>
                <CreateButton type={'drawer'} />
                <EnterCodeButton type={'drawer'} />
              </>} data-sentry-element="MobileDrawer" data-sentry-source-file="GnbHeader.tsx" />
        </NoSSR>
      </div>
    </HeaderContainer>;
}